import { saveAs } from "file-saver";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Divider, Icon, Loader } from "semantic-ui-react";

import { DATE_FORMATS_FNS, UploadVideo } from "components";
import "components/Videos/Videos.scss";
import ENV from "config/Env";
import { getGcsDownloadURL } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, DealerLocation, QuestionResultVideo } from "models";

type VideosProps = {
  videos?: QuestionResultVideo[];
  appointment?: Appointment;
  location?: DealerLocation | null;
  isEditable?: boolean;
  handleDeleteVideo?: (data: QuestionResultVideo) => void;
  handleVideoUpload?: (data: QuestionResultVideo, url: string) => void;
  handleVideoVisibility?: (data: QuestionResultVideo) => void;
};

export const Videos = ({ videos, appointment, location, isEditable, handleDeleteVideo, handleVideoUpload, handleVideoVisibility }: VideosProps) => {
  const { t } = useTranslation();

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  if (!videos?.length || !appointment) return null;

  const handleSaveVideo = async (video: QuestionResultVideo) => {
    const videoName = `Car_${appointment.reg_number_escaped}_WO_${appointment.wo_nr}_${formatDate(new Date(), DATE_FORMATS_FNS.yearMonthDate)}_${video.id}.mp4`;
    setDownloadingId(video.id);

    try {
      const response = await fetch(getGcsDownloadURL(video.url));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      saveAs(await response.blob(), videoName);
    } catch (_err) {
      toast.error(t("v8_failed_to_download_video", "Failed to download video"));
    }

    setDownloadingId(curr => (curr === video.id ? null : curr));
  };

  const uploadPath = `${location?.id}_${location?.name}/${formatDate(appointment.created_on, DATE_FORMATS_FNS.yearMonthDateSlash)}/${appointment.id}_${appointment.wo_nr}`;

  return (
    <div className="Snooze-Videos">
      {videos.map((video, index) => {
        return (
          <div key={video.id} className="video-view">
            <video controls key={video.url}>
              <source src={video.url} type="video/mp4" />
              <source src={video.url} type="video/quicktime" />
            </video>
            <Divider />
            <div className="video-actions">
              {isEditable && handleVideoVisibility && (
                <div className="video-action-button" onClick={() => handleVideoVisibility(video)}>
                  <Icon className={`eye ${video.visible_to_customer ? "green" : "slash red"}`} />
                </div>
              )}
              {location?.is_editing_question_videos_enabled && handleVideoUpload && (
                <div className="video-action-button">
                  <UploadVideo
                    uploadPath={uploadPath}
                    path="/checks/file/upload"
                    baseURL={ENV.appointmentBaseURL}
                    formId={`upload-video-${video.id}-${index}`}
                    onSuccess={url => handleVideoUpload(video, url)}
                  />
                </div>
              )}
              <div className="video-action-button" onClick={() => downloadingId !== video.id && handleSaveVideo(video)}>
                {downloadingId === video.id ? <Loader active inline size="tiny" /> : <Icon className="download blue" />}
              </div>
              {isEditable && handleDeleteVideo && (
                <div className="video-action-button" onClick={() => handleDeleteVideo(video)}>
                  <Icon className="trash grey" />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
